// SUMMARY: Shared Shiki highlighter — lazy singleton with light/dark themes and on-demand language loading

import { createHighlighter, bundledLanguages, type BundledLanguage, type Highlighter } from 'shiki'

const LIGHT_THEME = 'github-light'
const DARK_THEME = 'github-dark'
const BASE_LANGS = ['javascript', 'typescript', 'tsx', 'json', 'bash', 'python', 'html', 'css', 'sql', 'markdown']

const LANG_ALIASES: Record<string, string> = {
	sh: 'bash',
	shell: 'bash',
	zsh: 'bash',
	js: 'javascript',
	jsx: 'javascript',
	ts: 'typescript',
	py: 'python',
	yml: 'yaml',
	'c++': 'cpp',
	'c#': 'csharp',
	rs: 'rust',
	go: 'go',
	text: 'text',
	plaintext: 'text',
}

let highlighterPromise: Promise<Highlighter> | null = null

function getHighlighter(): Promise<Highlighter> {
	if (!highlighterPromise) {
		highlighterPromise = createHighlighter({ themes: [LIGHT_THEME, DARK_THEME], langs: BASE_LANGS })
	}
	return highlighterPromise
}

function normalizeLang(lang?: string): string {
	if (!lang) return 'text'
	const lower = lang.toLowerCase()
	return LANG_ALIASES[lower] ?? lower
}

/**
 * Highlight a code snippet to dual-theme HTML (CSS variables drive light/dark).
 * Unknown languages fall back to plain text so highlighting never throws.
 *
 * @example
 * highlightCode({ code: 'print("hi")', lang: 'py' }) → '<pre class="shiki ...">…</pre>'
 */
export async function highlightCode(options: { code: string; lang?: string }): Promise<string> {
	const highlighter = await getHighlighter()
	let lang = normalizeLang(options.lang)

	if (lang !== 'text' && !highlighter.getLoadedLanguages().includes(lang)) {
		if (lang in bundledLanguages) {
			try {
				await highlighter.loadLanguage(lang as BundledLanguage)
			} catch {
				lang = 'text'
			}
		} else {
			lang = 'text'
		}
	}

	return highlighter.codeToHtml(options.code, {
		lang,
		themes: { light: LIGHT_THEME, dark: DARK_THEME },
		defaultColor: false,
	})
}
